import React from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { documentTipsIllustrationStyles, documentTipsModalStyles } from './DocumentTipsModal.styles';
import panDocumentTipsIllustration from 'resources/img/pan-document-tips.jpg';
import chequeDocumentTipsIllustration from 'resources/img/cheque-document-tips.jpg';

type DocumentType = 'pan' | 'cheque';
type TipAction = 'camera' | 'upload';

interface DocumentTipsModalProps {
  open: boolean;
  documentType: DocumentType;
  action: TipAction;
  onClose: () => void;
  onGotIt: () => void;
}

const getTitle = (documentType: DocumentType) => {
  if (documentType === 'pan') {
    return 'Tips for a clear PAN card photo';
  }

  return 'Tips for clear cheque photo';
};

const getExampleTitle = (documentType: DocumentType) => {
  if (documentType === 'pan') {
    return 'Example of a PAN photo:';
  }

  return 'Example of a cancelled cheque photo:';
};

const getBullets = (documentType: DocumentType, action: TipAction) => {
  const cornerWord = action === 'camera' ? 'frame' : 'image';

  if (documentType === 'pan') {
    return [
      `All four corners of the PAN card should be visible in the ${cornerWord}.`,
      'PAN number and name should be clearly readable.',
      'Avoid shadows or glare on the PAN.',
    ];
  }

  return [
    `All four corners of the cheque should be visible in the ${cornerWord}.`,
    'Account number and IFSC code should not be overlapped by the "Cancelled" marking.',
    'Avoid shadows or glare on the cheque.',
  ];
};

const DocumentIllustration = ({ documentType, action }: { documentType: DocumentType; action: TipAction }) => {
  const isPan = documentType === 'pan';
  const showCameraFrame = action === 'camera';
  const illustrationSrc = isPan ? panDocumentTipsIllustration : chequeDocumentTipsIllustration;

  return (
    <Box
      className="document-tips-modal-illustration-root"
      sx={documentTipsIllustrationStyles.root}
    >
      <Box
        component="img"
        className="document-tips-modal-illustration-image"
        src={illustrationSrc}
        alt={isPan ? 'PAN card sample' : 'Cancelled cheque sample'}
        sx={documentTipsIllustrationStyles.image}
      />

      {showCameraFrame && (
        <>
          <Box
            className="document-tips-modal-illustration-corner-top-left"
            sx={documentTipsIllustrationStyles.cornerTopLeft}
          />
          <Box
            className="document-tips-modal-illustration-corner-top-right"
            sx={documentTipsIllustrationStyles.cornerTopRight}
          />
          <Box
            className="document-tips-modal-illustration-corner-bottom-left"
            sx={documentTipsIllustrationStyles.cornerBottomLeft}
          />
          <Box
            className="document-tips-modal-illustration-corner-bottom-right"
            sx={documentTipsIllustrationStyles.cornerBottomRight}
          />
        </>
      )}
    </Box>
  );
};

export function DocumentTipsModal({
  open,
  documentType,
  action,
  onClose,
  onGotIt,
}: DocumentTipsModalProps) {
  const bullets = getBullets(documentType, action);

  return (
    <Dialog
      className="document-tips-modal-dialog"
      open={open}
      onClose={onClose}
      PaperProps={{
        className: 'document-tips-modal-paper',
        sx: documentTipsModalStyles.paper,
      }}
    >
      <IconButton
        className="document-tips-modal-floating-close-button"
        onClick={onClose}
        sx={documentTipsModalStyles.floatingCloseButton}
        aria-label="Close tips"
      >
        <svg className="document-tips-modal-floating-close-icon" fill="none" viewBox="0 0 14 14" width="22" height="22">
          <path
            className="document-tips-modal-floating-close-icon-path"
            d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
            fill="#4B5563"
          />
        </svg>
      </IconButton>

      <Box
        className="document-tips-modal-container"
        sx={documentTipsModalStyles.container}
      >
        <Box className="document-tips-modal-drag-handle-row" sx={documentTipsModalStyles.dragHandleRow}>
          <Box className="document-tips-modal-drag-handle" sx={documentTipsModalStyles.dragHandle} />
        </Box>

        <Box className="document-tips-modal-header" sx={documentTipsModalStyles.header}>
          <Box className="document-tips-modal-title-wrap" sx={documentTipsModalStyles.titleWrap}>
            <Typography
              className="document-tips-modal-title"
              sx={documentTipsModalStyles.title}
            >
              {getTitle(documentType)}
            </Typography>
          </Box>
        </Box>

        <Box className="document-tips-modal-example-box" sx={documentTipsModalStyles.exampleBox}>
          <Typography
            className="document-tips-modal-example-title"
            sx={documentTipsModalStyles.exampleTitle}
          >
            {getExampleTitle(documentType)}
          </Typography>
          <DocumentIllustration documentType={documentType} action={action} />
        </Box>

        <Box className="document-tips-modal-bullets" component="ol" sx={documentTipsModalStyles.bullets}>
          {bullets.map((bullet, index) => (
            <Typography
              className="document-tips-modal-bullet-item"
              key={`${documentType}-${action}-${index}`}
              component="li"
              sx={documentTipsModalStyles.bulletItem}
            >
              {bullet}
            </Typography>
          ))}
        </Box>

        <Button
          className="document-tips-modal-got-it-button"
          onClick={onGotIt}
          variant="contained"
          fullWidth
          sx={documentTipsModalStyles.gotItButton}
        >
          Got It
        </Button>
      </Box>
    </Dialog>
  );
}